import React, { useCallback, useMemo, useState } from 'react';
import { Box, DialogActions, DialogContent } from '@mui/material';

import { AppButton } from '../app/button/appButton';
import { AppAutocomplete } from '../app/input/appAutocomplete';
import { ROLES_ID } from '../../models/employee';
import { useGetEmployeesQuery } from '../../store/employees/employeesService';

export interface EmployeeOption {
  id: string;
  label: string;
}

interface Props {
  handleClose(): void;
  handleDisplay(employee: EmployeeOption): void;
  managerId?: string;
}

const defaultQueryParams = {
  includeDeleted: true,
  includeInactive: true,
  take: 20,
};

const style = {
  dialogActions: {
    p: 0,
    mt: 2,
  },
  teamLeadContainer: {
    mt: 4,
    mb: 6,
  },
} as const;

export function ReportsTeamLeadSelect({
  handleClose,
  managerId,
  handleDisplay,
}: Props): JSX.Element {
  const [teamLead, setTeamLead] = useState<EmployeeOption | null>(null);
  const [filter, setFilter] = useState<string>('');

  const { data: teamLeads, isFetching: isFetchingEmployees } =
    useGetEmployeesQuery({
      ...defaultQueryParams,
      teamLeadGuid: managerId,
      roleId: ROLES_ID.TEAM_LEAD,
      filter,
    });

  const teamLeadsOptions = useMemo(() => {
    if (teamLeads) {
      return teamLeads.items.map((item) => ({
        id: item.id,
        label: item.name,
      }));
    }

    return [];
  }, [teamLeads]);

  const selectTeamLead = useCallback(
    (_: React.BaseSyntheticEvent, value) => {
      setTeamLead(value);
    },
    [teamLeadsOptions]
  );

  const handleDisplayEmployees = useCallback(() => {
    if (teamLead) {
      handleDisplay(teamLead);
      handleClose();
    }
  }, [teamLead, handleDisplay, handleClose]);

  return (
    <Box>
      <DialogContent sx={style.teamLeadContainer}>
        <AppAutocomplete
          label="Team Lead"
          name="teamLead"
          options={teamLeadsOptions}
          handleChange={selectTeamLead}
          isLoading={isFetchingEmployees}
          searchOptions={setFilter}
        />
      </DialogContent>
      <DialogActions sx={style.dialogActions}>
        <AppButton label="Cancel" onClick={handleClose} />
        <AppButton
          label="Display"
          isPrimary
          buttonType="submit"
          disabled={!teamLead}
          onClick={handleDisplayEmployees}
        />
      </DialogActions>
    </Box>
  );
}
